/**
 * The device's pre-lock fields as a read-only definition list, shown as the
 * "Device details" card once they can no longer be edited — so staff can
 * check what is on file without going back to the session.
 *
 * Also owns the message an online device shows while it waits for earlier
 * steps: an in-store device never waits (the machine is on the counter — see
 * `pickLayout` / `inspectionStage.ts`), only a cancelled or expired one does.
 */

import React from 'react';
import type { CheckInDraftJob } from '@techgitter/contracts';

import { checkInDeviceLabel } from '../deviceTypes';

const AWAITING_MESSAGE: Record<string, string> = {
  intake_in_progress: 'The customer is filling in their details. Wait for them to accept the terms before continuing.',
  terms_accepted: 'Creating RepairShopr records — back in a moment.',
  creating_jobs: 'Creating RepairShopr records — back in a moment.',
  job_creation_failed: 'Job creation failed. Go back to the session and tap Retry.',
  awaiting_payment: 'Awaiting payment. Inspection can start once payment is recorded.',
  awaiting_bank_transfer: 'Awaiting bank transfer. Inspection can start once payment is recorded.',
  payment_failed: 'Payment failed. Retry payment from the session page.',
  cancelled: 'This check-in was cancelled. Inspection answers are no longer accepted.',
  expired: 'This check-in expired. Recover it from the session page before inspecting.',
};

/** Why the inspection isn't available yet, for a device that has to wait. */
export function awaitingMessage(lifecycle: string | null | undefined): string {
  return AWAITING_MESSAGE[lifecycle ?? ''] ?? 'Awaiting earlier steps to complete.';
}

export const DeviceOnFile: React.FC<{ draftJob: CheckInDraftJob }> = ({ draftJob }) => (
  <div className="ci-def ci-def--wide">
    <DefRow label="Device" value={checkInDeviceLabel(draftJob)} />
    <DefRow label="Service Summary" value={draftJob.serviceSummary} />
    <DefRow label="Requirements" value={draftJob.requirements} />
    <DefRow label="Customer Details" value={draftJob.customerVisibleDetails} />
    <DefRow label="Internal Notes" value={draftJob.internalNotes} />
  </div>
);

const DefRow: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => (
  <>
    <span className="ci-def__label">{label}</span>
    <span className={`ci-def__value ci-pre-wrap${value ? '' : ' ci-def__value--empty'}`}>
      {value || '—'}
    </span>
  </>
);
